// Audio Overviews (ORA-735): two-host podcast episodes generated from the
// lectures a student selects. These types mirror the ora-be contract
// (`/api/v5/audio-overviews`); keep the spellings identical on both sides.

export const AUDIO_OVERVIEW_FORMATS = [
  'DEEP_DIVE',
  'BRIEF',
  'BOARD_REVIEW',
  'DEBATE',
] as const;

export type AudioOverviewFormat = (typeof AUDIO_OVERVIEW_FORMATS)[number];

export const AUDIO_OVERVIEW_LENGTHS = ['SHORT', 'DEFAULT', 'LONG'] as const;

/** BRIEF ignores the length; the server stores `DEFAULT` for it. */
export type AudioOverviewLength = (typeof AUDIO_OVERVIEW_LENGTHS)[number];

export const AUDIO_OVERVIEW_STATUSES = [
  'QUEUED',
  'SCRIPTING',
  'SYNTHESIZING',
  'STITCHING',
  'READY',
  'FAILED',
] as const;

export type AudioOverviewStatus = (typeof AUDIO_OVERVIEW_STATUSES)[number];

export type AudioOverviewSourceKind = 'lecture' | 'article';

export type ArticleAudioOverviewBlockedBy =
  | 'locked'
  | 'daily_limit'
  | 'in_progress_limit'
  | 'article_daily_limit'
  | 'article_in_progress_limit';

export interface AudioOverviewLectureRef {
  id: string;
  title: string;
}

export interface AudioOverviewSummary {
  id: string;
  /** Older servers omit this; the client parser defaults it to `lecture`. */
  source: AudioOverviewSourceKind;
  /**
   * A shared episode: a Library article's, or one Ora made in advance for a
   * lecture. Nobody owns it, so it is never deleted or retried by a student,
   * and listening progress is the caller's own. Older servers omit it; the
   * parser then treats article episodes as shared.
   */
  shared: boolean;
  articleId: string | null;
  articleTitle: string | null;
  /** Written by the script model; null until scripting finishes. */
  title: string | null;
  format: AudioOverviewFormat;
  length: AudioOverviewLength;
  focusNotes: string | null;
  status: AudioOverviewStatus;
  /** 0–100, monotonic. */
  progress: number;
  /** Set when READY. */
  durationSec: number | null;
  lectures: AudioOverviewLectureRef[];
  createdAt: string;
  readyAt: string | null;
  /** e.g. `GENERATION_FAILED`, `NO_SOURCE_CONTENT`, `TIMED_OUT`. */
  errorCode: string | null;
  /** Saved resume position. */
  positionSec: number | null;
  /** Listened to the end. */
  completedAt: string | null;
  /** The caller is on the free plan: metadata only, no audio. */
  locked: boolean;
  /**
   * The server will accept `POST /:id/retry`. Older servers omit it; the
   * parser then falls back to `status === 'FAILED'`.
   */
  retryable: boolean;
}

export interface ArticleAudioOverviewResponse {
  overview: AudioOverviewSummary | null;
  locked: boolean;
  canGenerate: boolean;
  blockedBy: ArticleAudioOverviewBlockedBy | null;
}

/** `GET /lectures/:lectureId`: the lecture's ready-made shared episode. */
export interface LectureAudioOverviewResponse {
  overview: AudioOverviewSummary | null;
  locked: boolean;
}

export interface CreateArticleAudioOverviewResponse {
  overview: AudioOverviewSummary;
  status: 200 | 202;
}

export interface AudioOverviewConfig {
  canGenerate: boolean;
  /** The caller is on the free plan (no active paid plan, sub or trial). */
  locked: boolean;
  dailyLimit: number;
  usedToday: number;
  inProgress: number;
  maxInProgress: number;
  maxLectures: number;
  maxFocusChars: number;
  resetsAt: string;
}

export interface CreateAudioOverviewInput {
  lectureIds: string[];
  examId?: string;
  format: AudioOverviewFormat;
  length?: AudioOverviewLength;
  focusNotes?: string;
}

export interface AudioOverviewListPage {
  items: AudioOverviewSummary[];
  nextCursor: string | null;
}

export interface AudioOverviewAudioSource {
  url: string;
  expiresAt: string;
  durationSec: number;
  mimeType: string;
}

/** Host A explains; host B is the curious student. The hosts have no names. */
export type AudioOverviewSpeaker = 'A' | 'B';

/** One spoken turn of an episode's script (`GET /:id/script`). */
export interface AudioOverviewScriptTurn {
  /** Global index over the whole script; `timing` counts turns by it. */
  index: number;
  speaker: AudioOverviewSpeaker;
  text: string;
  /** Full fact ids (`<documentModelId>:f042`); empty when the turn cites none. */
  factIds: string[];
  /**
   * Estimated start in the episode (chunk timing shared out by spoken words,
   * about ±15 s); null without timing.
   */
  startSec: number | null;
}

export interface AudioOverviewScriptSegment {
  heading: string;
  turns: AudioOverviewScriptTurn[];
}

/** One synthesized chunk: turns `turnStart`–`turnEnd` (inclusive). */
export interface AudioOverviewScriptTiming {
  turnStart: number;
  turnEnd: number;
  startSec: number;
  endSec: number;
}

/** The transcript behind an episode (Document Pipeline v4, ORA-798/799). */
export interface AudioOverviewScript {
  id: string;
  title: string;
  status: AudioOverviewStatus;
  segments: AudioOverviewScriptSegment[];
  /** Null until the episode is READY, and for episodes made before timing. */
  timing: AudioOverviewScriptTiming[] | null;
  /**
   * `documentModelId` → `lectureId` for the models the turns cite. A
   * lecture's current model comes before the model it had when the script
   * was written, if that differs.
   */
  lectures: Record<string, string>;
}

export const isAudioOverviewTerminal = (status: AudioOverviewStatus): boolean =>
  status === 'READY' || status === 'FAILED';

/** True once `resetsAt` has passed: the config's daily counts are stale. */
export const isAudioOverviewConfigPastReset = (
  config: Pick<AudioOverviewConfig, 'resetsAt'>,
  now: number = Date.now()
): boolean => {
  const resetsAt = Date.parse(config.resetsAt);
  return Number.isFinite(resetsAt) && resetsAt <= now;
};
